// # Dialog — 화면을 막는 창 하나(docs/design.md)
//
// 네이티브 `<dialog>` 의 `showModal()` 이 접근성의 무거운 일을 전부 한다: 포커스를 창 안에 가두고,
// 뒤를 `inert` 로 만들고, 맨 위 층(top layer)에 올리고, Esc 를 `cancel` 사건으로 준다. 우리가 하는
// 일은 **열림을 props 하나로 잇는 것**뿐이다.
//
// - 열림은 부르는 쪽이 쥔다(`open`·`on_close`). Esc·바깥 누르기·닫기 단추는 전부 `on_close` 를
//   부를 뿐 스스로 닫지 않는다 — 부르는 쪽이 "쓰다 만 글이 있다" 며 거절할 수 있어야 한다.
//   크롬은 사람의 손짓 없이 Esc 를 두 번 받으면 막아도 닫는다. 그때 오는 `close` 도 `on_close` 로 잇는다.
// - 바깥 누르기는 **누른 곳과 뗀 곳이 둘 다** 막(backdrop)일 때만 닫는다. 칸 안에서 글자를 긁다가
//   바깥에서 손을 떼면 창이 닫혀 쓰던 것이 날아가는 흔한 사고를 막는다. `dismissable={false}` 면 끈다.
// - 닫힌 창의 안은 그리지 않는다. 숨겨 둔 폼이 질의를 걸거나 지난 값을 들고 있으면 안 된다.
// - 제목(`title`)은 필수고 `aria-labelledby` 로 창의 이름이 된다. 설명은 `aria-describedby`.

import { X } from 'lucide-react'
import {
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useId,
  useRef,
} from 'react'
import { useT } from '../i18n.ts'
import { IconButton } from './button.tsx'
import { cx } from './cx.ts'

export type DialogSize = 'sm' | 'md' | 'lg'

export interface DialogProps {
  open: boolean
  /** Esc·바깥 누르기·닫기 단추가 부른다. 닫을지는 부르는 쪽이 정한다. */
  on_close: () => void
  /** 창의 이름. 머리에 서고 스크린리더가 창에 들어설 때 읽는다. */
  title: ReactNode
  /** 제목 밑 한 줄. */
  description?: ReactNode
  /** 아래 단추 줄. 오른쪽 끝으로 모인다 — 거절이 왼쪽, 허락이 오른쪽. */
  footer?: ReactNode
  /** 기본 `md`(520px). `sm` 은 확인 창(400px), `lg` 는 설정·앱 창(720px). */
  size?: DialogSize
  /** 기본 참. 거짓이면 바깥을 눌러도 안 닫힌다(Esc·닫기 단추는 된다). */
  dismissable?: boolean
  className?: string
  children?: ReactNode
}

/** 막을 눌렀는가 — `<dialog>` 가 사건의 과녁이면 안쪽 판이 아니라 막이다. */
export function on_backdrop(e: { target: unknown; currentTarget: unknown }): boolean {
  return e.target === e.currentTarget
}

export function Dialog(props: DialogProps) {
  const t = useT()
  const ref = useRef<HTMLDialogElement>(null)
  const down_outside = useRef(false)
  // 연 자리. 창이 `.close()` 없이 통째로 빠지면(부르는 쪽이 `open` 대신 창을 걷는 모양) 브라우저가 초점을
  // 돌려주지 못한다 — 👥 로 연 채널 창을 Esc 로 닫으면 초점이 👥 가 아니라 입력칸에 갔다(3차 F278).
  const opener = useRef<HTMLElement | null>(null)
  const base = useId()
  const title_id = `${base}-title`
  const description_id = `${base}-description`
  const { open, on_close } = props

  useEffect(() => {
    const el = ref.current
    if (el === null) return
    if (open && !el.open) {
      const now = el.ownerDocument.activeElement
      opener.current = now instanceof HTMLElement && now !== el.ownerDocument.body ? now : null
      el.showModal()
      first_focus(el)
    }
    if (!open && el.open) el.close()
  }, [open])
  // 연 채로 통째로 빠지면(나간 채널의 확인 창) `close` 사건도 없이 초점이 바닥에 떨어진다 — 그 뒤에 집으로.
  useEffect(() => {
    const doc = ref.current?.ownerDocument
    return () => {
      setTimeout(() => go_home(doc, opener.current), 0)
    }
  }, [])

  const cancel = (e: SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault()
    on_close()
  }
  // 우리가 닫아서 온 `close` 면 `open` 이 이미 거짓이다. 참인데 왔으면 브라우저가 닫은 것.
  const closed = () => {
    if (open) on_close()
    go_home(ref.current?.ownerDocument, opener.current)
  }
  const pointer_down = (e: PointerEvent<HTMLDialogElement>) => {
    down_outside.current = on_backdrop(e)
  }
  const click = (e: MouseEvent<HTMLDialogElement>) => {
    const outside = down_outside.current && on_backdrop(e)
    down_outside.current = false
    if (outside && props.dismissable !== false) on_close()
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 막 누르기의 키보드 짝은 Esc(`cancel`)다.
    <dialog
      ref={ref}
      className={cx('Dialog', props.className)}
      data-size={props.size ?? 'md'}
      aria-labelledby={title_id}
      aria-describedby={props.description ? description_id : undefined}
      onCancel={cancel}
      onClose={closed}
      onPointerDown={pointer_down}
      onClick={click}
    >
      {open && (
        <div className="Dialog-panel">
          <header className="Dialog-header">
            <div className="Dialog-heading">
              <h2 className="Dialog-title" id={title_id}>
                {props.title}
              </h2>
              {props.description && (
                <p className="Dialog-description" id={description_id}>
                  {props.description}
                </p>
              )}
            </div>
            <IconButton
              className="Dialog-close"
              size="sm"
              label={t('닫기')}
              icon={<X aria-hidden />}
              onClick={on_close}
            />
          </header>
          <div className="Dialog-body">{props.children}</div>
          {props.footer && <footer className="Dialog-footer">{props.footer}</footer>}
        </div>
      )}
    </dialog>
  )
}

/**
 * 창이 닫히면 브라우저는 연 자리로 초점을 돌려주는데, 창이 통째로 빠졌으면 그러지 못하고, 그 자리가 창 사이에
 * 사라졌으면(채널 나가기 → 다른 채널) 초점이 문서 바닥에 떨어져 아무 키도 안 먹는다(3차 F262). 초점이 바닥일
 * 때만 — 연 자리가 아직 있으면 거기로(F278), 없거나 초점을 못 받으면 집(`data-home` — 입력칸)으로 간다.
 */
function go_home(doc: Document | undefined, opener: HTMLElement | null = null): void {
  if (doc === undefined) return
  const on_floor = () => doc.activeElement === null || doc.activeElement === doc.body
  if (!on_floor()) return
  if (opener?.isConnected) opener.focus({ preventScroll: true })
  if (on_floor()) doc.querySelector<HTMLElement>('[data-home]')?.focus()
}

/**
 * 창이 열리면 브라우저는 첫 초점을 머리의 닫기(X)에 준다. 키보드로 연 창이면 그 초점이 `:focus-visible`
 * 이라 "닫기" 풍선이 곧장 떠 제목과 X 를 가리고(3차 F35), 할 일은 X 가 아니다. 칸이 있으면 첫 칸, 없으면
 * 아래 줄의 첫 단추(확인 창이면 취소 — 덜 위험한 쪽)로 옮긴다. 이미 창 안의 다른 곳에 초점이 갔으면 둔다.
 */
function first_focus(el: HTMLDialogElement): void {
  const now = el.ownerDocument.activeElement
  if (now !== null && now !== el && !now.classList.contains('Dialog-close')) return
  const target = el.querySelector<HTMLElement>(
    '.Dialog-body [autofocus], .Dialog-body input:not([type=hidden]):not(:disabled), .Dialog-body textarea:not(:disabled), .Dialog-body select:not(:disabled), .Dialog-footer button:not(:disabled)',
  )
  target?.focus()
}
